import type { HTMLAttributes } from 'react';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import type { AppLanguage } from '../../lib/localization';
import type { StatusBadgeValue } from '../../lib/documentStatus';

export interface StatusBadgeProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  status: StatusBadgeValue;
  language?: AppLanguage;
  showDot?: boolean;
}

const STATUS_PRESENTATION: Record<StatusBadgeValue, {
  en: string;
  ar: string;
  className: string;
}> = {
  draft: { en: 'Draft', ar: 'مسودة', className: 'border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200' },
  done: { en: 'Done', ar: 'مؤكد', className: 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300' },
  cancelled: { en: 'Cancelled', ar: 'ملغي', className: 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/50 dark:text-rose-300' },
  nothing_to_invoice: { en: 'Nothing to invoice', ar: 'لا يوجد ما يُفوتر', className: 'border-slate-300 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300' },
  to_invoice: { en: 'To invoice', ar: 'جاهز للفوترة', className: 'border-sky-300 bg-sky-50 text-sky-700 dark:border-sky-800 dark:bg-sky-950/50 dark:text-sky-300' },
  partially_invoiced: { en: 'Partially invoiced', ar: 'مفوتر جزئياً', className: 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-300' },
  fully_invoiced: { en: 'Fully invoiced', ar: 'مفوتر بالكامل', className: 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300' },
  nothing_to_deliver: { en: 'Nothing to deliver', ar: 'لا يوجد ما يُسلّم', className: 'border-slate-300 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300' },
  to_deliver: { en: 'To deliver', ar: 'جاهز للتسليم', className: 'border-sky-300 bg-sky-50 text-sky-700 dark:border-sky-800 dark:bg-sky-950/50 dark:text-sky-300' },
  partially_delivered: { en: 'Partially delivered', ar: 'مسلّم جزئياً', className: 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-300' },
  fully_delivered: { en: 'Fully delivered', ar: 'مسلّم بالكامل', className: 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300' },
  unpaid: { en: 'Unpaid', ar: 'غير مدفوع', className: 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/50 dark:text-rose-300' },
  partially_paid: { en: 'Partially paid', ar: 'مدفوع جزئياً', className: 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-300' },
  paid: { en: 'Paid', ar: 'مدفوع', className: 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300' },
  overpaid: { en: 'Overpaid', ar: 'مدفوع بالزيادة', className: 'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-950/50 dark:text-violet-300' },
  pending: { en: 'Pending', ar: 'قيد الانتظار', className: 'border-slate-300 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300' },
  accepted: { en: 'Accepted', ar: 'مقبول', className: 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300' },
  declined: { en: 'Declined', ar: 'مرفوض', className: 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/50 dark:text-rose-300' },
  sent: { en: 'Sent', ar: 'مُرسل', className: 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-300' },
  expired: { en: 'Expired', ar: 'منتهي الصلاحية', className: 'border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950/50 dark:text-orange-300' },
};

export function StatusBadge({
  status,
  language = 'en',
  showDot = true,
  className,
  ...props
}: StatusBadgeProps) {
  const presentation = STATUS_PRESENTATION[status];

  return (
    <Badge
      variant="outline"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
      className={cn('gap-1.5 whitespace-nowrap', presentation.className, className)}
      {...props}
    >
      {showDot && <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />}
      {presentation[language]}
    </Badge>
  );
}
